import { Avatar } from 'src/design/atoms/Avatar';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { IconCheck, IconCopy, IconInfoCircle } from '@tabler/icons-react';
import { useClipboard } from '@mantine/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { IChatUiMessage } from 'src/hooks/useChat';
import {
  hasServerId,
  messageText,
} from 'src/api/queries/chat/useChatConversationQueries';
import { AssistantReplyFooter } from 'src/app/chat/AssistantReplyFooter';
import { emptyReplyNotice } from 'src/app/chat/emptyReplyNotice';
import { resolveEntityRefs } from 'src/app/chat/entityRefs';
import { stripMemoryMarkers } from 'src/app/chat/memoryMarkers';
import { MessageSegmentView } from 'src/app/chat/MessageSegmentView';
import { RUN_SQL_TOOL } from 'src/app/chat/SqlDisclosure';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { FireIcon } from 'src/design/atoms/FireIcon';
import { canRateMessage } from 'src/app/chat/messageFeedback';
import { MessageFeedbackActions } from 'src/app/chat/MessageFeedbackActions';
import { MessageFeedbackSummary } from 'src/app/chat/MessageFeedbackSummary';
import { MessageEditor } from 'src/app/chat/MessageEditor';
import { MessageQuestionActions } from 'src/app/chat/MessageQuestionActions';
import { ReplyRetryActions } from 'src/app/chat/ReplyRetryActions';
import classes from './chat.module.css';

const InfoNotice: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Group gap={4} wrap="nowrap" align="flex-start">
    <IconInfoCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
    <Text size="xs" c="dimmed">
      {children}
    </Text>
  </Group>
);

export interface IMessageBubbleProps {
  message: IChatUiMessage;
  /** True only for the most recent assistant message; gates the Regenerate action. */
  isLastAssistant?: boolean;
  /** True only for the most recent user message: it gets Edit, every older user message gets Fork. */
  isLastUser?: boolean;
  /** True while a reply streams or a fork is pending; hides Regenerate, Edit and Fork. */
  busy?: boolean;
  /** True while this message's fork is pending; its Fork icon stays, with a spinner. */
  forking?: boolean;
  onRegenerate?: () => void;
  /** Shown beside Regenerate only when the reply stopped at the tool limit. */
  onContinue?: () => void;
  onEdit?: (messageId: string, text: string) => void;
  onFork?: (messageId: string) => void;
  /** Enables the vote buttons and the query CSV downloads; without it a stored vote renders read-only. */
  conversationId?: string | null;
  /** Admin transcript view: memory cards show their stored snapshot and the Used memory chip is hidden. */
  readOnly?: boolean;
  onRefClick?: () => void;
}

/** @registry Single chat message bubble (user or assistant); renders markdown, interleaved SQL tool-activity disclosures, streaming/truncation notices, per-message copy/vote/regenerate/continue actions, and edit-and-resend (last user message) or fork (older user messages). */
export const MessageBubble = React.memo<IMessageBubbleProps>(
  ({
    message,
    isLastAssistant,
    isLastUser,
    busy,
    forking,
    onRegenerate,
    onContinue,
    onEdit,
    onFork,
    conversationId,
    readOnly,
    onRefClick,
  }) => {
    const clipboard = useClipboard({ timeout: 1500 });
    const [feedbackPopoverOpen, setFeedbackPopoverOpen] = useState(false);
    const [editing, setEditing] = useState(false);

    const isUser = message.role === 'user';
    const hasText = message.segments.some(
      (segment) =>
        segment.kind === 'text' &&
        (isUser
          ? segment.text
          : stripMemoryMarkers(segment.text, message.streaming)
        ).length > 0,
    );
    const hasChart = message.segments.some(
      (segment) => segment.kind === 'chart',
    );
    const hasTable = message.segments.some(
      (segment) => segment.kind === 'table',
    );
    const wasCutShort =
      !isUser && !message.streaming && message.stopReason === 'max_tokens';
    const emptyNotice =
      !isUser && !message.streaming && !hasText && !hasChart && !hasTable
        ? emptyReplyNotice(message)
        : null;

    const isSavedQuestion = isUser && hasServerId(message);
    const canEdit = isSavedQuestion && !busy && !!isLastUser && !!onEdit;
    const canFork =
      isSavedQuestion && (!busy || !!forking) && !isLastUser && !!onFork;
    const showActions = isUser ? hasText && !editing : !message.streaming;
    const showRegenerate = showActions && !isUser && isLastAssistant && !busy;
    const showContinue =
      showRegenerate &&
      !!onContinue &&
      message.stopReason === 'max_tool_iterations';
    const votingConversationId =
      conversationId && canRateMessage(message) ? conversationId : null;
    const storedFeedback = conversationId ? undefined : message.feedback;
    const pinActions =
      showContinue ||
      !!forking ||
      (!!votingConversationId && (!!message.feedback || feedbackPopoverOpen));
    const canExportQueries =
      !message.streaming && message.interruption !== 'failed';
    const queryNumberOffsets = useMemo(() => {
      let seen = 0;
      return message.segments.map((segment) => {
        const offset = seen;
        if (segment.kind === 'tools') {
          seen += segment.activity.filter(
            (item) => item.name === RUN_SQL_TOOL,
          ).length;
        }
        return offset;
      });
    }, [message.segments]);
    const rawText = useMemo(() => messageText(message), [message]);
    const copyText = useMemo(
      () => (isUser ? rawText : resolveEntityRefs(stripMemoryMarkers(rawText))),
      [isUser, rawText],
    );

    useEffect(() => {
      if (!canEdit) setEditing(false);
    }, [canEdit]);

    const submitEdit = (text: string) => {
      setEditing(false);
      onEdit?.(message.id, text);
    };

    const fillsRow = hasChart || hasTable || editing;

    return (
      <Box
        className={classes.messageRow}
        style={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
        }}
      >
        {/* A chart or table sizes to its container, so a bubble holding one must take the full row width instead of shrinking to its content. */}
        <Group
          align="flex-start"
          wrap="nowrap"
          gap="xs"
          maw="85%"
          w={fillsRow ? '85%' : undefined}
        >
          {!isUser && (
            <Avatar
              size="sm"
              radius="xl"
              color="blue"
              data-testid="assistant-avatar"
            >
              <FireIcon size={16} />
            </Avatar>
          )}
          <Stack
            gap={4}
            style={{ minWidth: 0, flex: fillsRow ? 1 : undefined }}
          >
            {editing ? (
              <MessageEditor
                initialText={rawText}
                onSubmit={submitEdit}
                onCancel={() => setEditing(false)}
              />
            ) : (
              <Paper
                p="sm"
                radius="md"
                bg={isUser ? 'blue.6' : 'gray.1'}
                c={isUser ? 'white' : 'bright'}
              >
                <Stack
                  gap="xs"
                  aria-live={message.streaming ? 'polite' : undefined}
                >
                  {message.segments.map((segment, index) => (
                    <MessageSegmentView
                      key={index}
                      segment={segment}
                      isUser={isUser}
                      conversationId={conversationId}
                      canExport={canExportQueries}
                      queryNumberOffset={queryNumberOffsets[index]}
                      readOnly={readOnly}
                      streaming={message.streaming}
                      onRefClick={onRefClick}
                    />
                  ))}
                  {message.streaming && !hasText && (
                    <Text size="sm" c="dimmed">
                      Thinking…
                    </Text>
                  )}
                  {wasCutShort && (
                    <InfoNotice>
                      This response was cut short because it reached its length
                      limit. Ask a narrower question to see the rest.
                    </InfoNotice>
                  )}
                  {emptyNotice && <InfoNotice>{emptyNotice}</InfoNotice>}
                  {!isUser && (
                    <AssistantReplyFooter
                      thoughtHarder={
                        !!message.thoughtHarder &&
                        !message.streaming &&
                        message.interruption !== 'failed'
                      }
                      usedMemoryIds={
                        readOnly || message.streaming
                          ? undefined
                          : message.usedMemoryIds
                      }
                    />
                  )}
                </Stack>
              </Paper>
            )}
            {showActions && (
              <Group
                gap={4}
                justify={isUser ? 'flex-end' : undefined}
                className={classes.actionRow}
                data-pinned={pinActions || undefined}
              >
                <BasicActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  disabled={copyText.length === 0}
                  label={clipboard.copied ? 'Copied' : 'Copy message'}
                  onClick={() => clipboard.copy(copyText)}
                >
                  {clipboard.copied ? (
                    <IconCheck size={16} />
                  ) : (
                    <IconCopy size={16} />
                  )}
                </BasicActionIcon>
                {votingConversationId && (
                  <MessageFeedbackActions
                    conversationId={votingConversationId}
                    messageId={message.id}
                    feedback={message.feedback}
                    onPopoverOpenChange={setFeedbackPopoverOpen}
                  />
                )}
                {isUser && (
                  <MessageQuestionActions
                    canEdit={canEdit}
                    canFork={canFork}
                    forking={!!forking}
                    onEdit={() => setEditing(true)}
                    onFork={() => onFork?.(message.id)}
                  />
                )}
                {showRegenerate && (
                  <ReplyRetryActions
                    onRegenerate={onRegenerate}
                    onContinue={showContinue ? onContinue : undefined}
                  />
                )}
              </Group>
            )}
            {storedFeedback && (
              <MessageFeedbackSummary feedback={storedFeedback} />
            )}
          </Stack>
        </Group>
      </Box>
    );
  },
);
